import { Player } from '@lottiefiles/react-lottie-player';
import {
  GetManyStatsResponsePrivate,
  StatDataDevices,
  StatDataRegions,
  StatDataSources,
  StatDataUserClickShowAnswers,
  StatDataUserFinishes,
  StatDataUserFinishesDuration,
  StatDataUserPerQuestionAnswerResponsePrivate,
  StatDataUserPerQuestionResponsePrivate,
  StatDataUserStarts,
  StatType,
  TestResponsePrivate,
} from '@tests/types';
import React, { FC, useMemo } from 'react';

import { Loader, UsersStats } from '@/assets';
import { StatsInfoText, StatsPieChart, StatsQuestionsBlock } from '@/components';
import { formatDuration, formatNumberWithSpaces, isCrossword } from '@/utils';

import { ContentBlock } from '../ContentBlock';
import { StatsInfoBlock } from '../StatsInfoBlock';
import styles from './styles.module.scss';

interface Props {
  statData: GetManyStatsResponsePrivate<string>;
  test: TestResponsePrivate<string>;
}

const isNotEmptyArray = (array: any[]) => array.length > 0;

export const TestStatsContainer: FC<Props> = ({ statData, test }) => {
  const userStarts = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.UserStarts)?.data || {
      count: 0,
    }) as StatDataUserStarts;

    return formatNumberWithSpaces(data.count);
  }, [statData]);

  const userFinishes = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.UserFinishes)?.data || {
      count: 0,
    }) as StatDataUserFinishes;

    return formatNumberWithSpaces(data.count);
  }, [statData]);

  const userClickShowAnswers = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.UserClickShowAnswers)
      ?.data || {
      count: 0,
    }) as StatDataUserClickShowAnswers;

    return formatNumberWithSpaces(data.count);
  }, [statData]);

  const userFinishesDuration = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.UserFinishesDuration)
      ?.data || { duration: 0 }) as StatDataUserFinishesDuration;

    return formatDuration(data?.duration);
  }, [statData]);

  const userPerQuestion = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.UserPerQuestion)
      ?.data || { questions: [] }) as StatDataUserPerQuestionResponsePrivate;

    return data;
  }, [statData]);

  const userPerQuestionAnswers = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.UserPerQuestionAnswer)
      ?.data || { questions: [] }) as StatDataUserPerQuestionAnswerResponsePrivate;

    return data;
  }, [statData]);

  const devices = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.Devices)?.data || {
      items: [],
    }) as StatDataDevices;

    return data;
  }, [statData]);

  const regions = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.Regions)?.data || {
      items: [],
    }) as StatDataRegions;

    return data;
  }, [statData]);

  const sources = useMemo(() => {
    const data = (statData?.results.find((item) => item.type === StatType.Sources)?.data || {
      items: [],
    }) as StatDataSources;

    return data;
  }, [statData]);

  if (!statData) {
    return (
      <div className={styles.loaderWrapper}>
        <Player autoplay loop src={Loader} className={styles.loader} />
      </div>
    );
  }

  return (
    <>
      {statData?.count > 0 && (
        <>
          <ContentBlock title="Общая информация">
            <div className={styles.statsTop}>
              <div className={styles.grid}>
                <StatsInfoBlock title="Среднее время прохождения">
                  <StatsInfoText text={userFinishesDuration} />
                </StatsInfoBlock>
                <StatsInfoBlock title="Начали прохождение">
                  <StatsInfoText text={userStarts} icon={<UsersStats />} />
                </StatsInfoBlock>
                <StatsInfoBlock title="Завершили прохождение">
                  <StatsInfoText text={userFinishes} icon={<UsersStats />} />
                </StatsInfoBlock>
                {isCrossword(test.type) && (
                  <StatsInfoBlock title="Нажали кнопку «Ответы»">
                    <StatsInfoText text={userClickShowAnswers} icon={<UsersStats />} />
                  </StatsInfoBlock>
                )}
              </div>
              {!isCrossword(test.type) && (
                <StatsInfoBlock title="Количество пользователей на каждом вопросе" noPadding>
                  <StatsQuestionsBlock
                    userPerQuestion={userPerQuestion}
                    userPerQuestionAnswers={userPerQuestionAnswers}
                  />
                </StatsInfoBlock>
              )}
            </div>
          </ContentBlock>
          {(isNotEmptyArray(devices.items) ||
            isNotEmptyArray(regions.items) ||
            isNotEmptyArray(sources.items)) && (
            <ContentBlock title="Информация о трафике">
              <div className={styles.statsBottom}>
                {isNotEmptyArray(sources.items) && (
                  <StatsInfoBlock title="Источник трафика">
                    <StatsPieChart data={sources.items} />
                  </StatsInfoBlock>
                )}
                {isNotEmptyArray(regions.items) && (
                  <StatsInfoBlock title="Регион">
                    <StatsPieChart data={regions.items} />
                  </StatsInfoBlock>
                )}
                {isNotEmptyArray(devices.items) && (
                  <StatsInfoBlock title="Типы устройств">
                    <StatsPieChart data={devices.items} />
                  </StatsInfoBlock>
                )}
              </div>
            </ContentBlock>
          )}
        </>
      )}
    </>
  );
};
